import { Language } from '../types';

export type RouteKey = 
  | 'home' 
  | 'rutas' 
  | 'routes' 
  | 'roteiros'
  | 'eventos' 
  | 'events'
  | 'consejos-manejo' 
  | 'driving-guide'
  | 'guia-direcao'
  | 'consejos-viaje'
  | 'travel-tips'
  | 'dicas-viagem';

export type SectionKey = 'home' | 'rutas' | 'eventos' | 'consejos-manejo' | 'consejos-viaje';

export interface PageSEO {
  title: string;
  description: string;
}

export const ROUTE_SEO: Record<SectionKey, Record<Language, PageSEO>> = {
  home: {
    es: {
      title: 'Cancún México | Guía Turística y Renta de Autos',
      description: 'Guía turística de Cancún y Riviera Maya: descubre playas, cenotes, zonas mayas y la mejor renta de autos con Easy Way sin cargos ocultos.'
    },
    en: {
      title: 'Cancun Mexico Travel | Official Guide & Car Rental',
      description: 'Complete travel guide to Cancun & Riviera Maya: beaches, cenotes, Mayan ruins and reliable car rental with Easy Way.'
    },
    pt: {
      title: 'Cancún México | Guia Turístico e Aluguel de Carros',
      description: 'Guia turístico de Cancún e Riviera Maya: praias, cenotes, ruínas maias e aluguel de carros seguro com Easy Way.'
    }
  },
  rutas: {
    es: {
      title: 'Rutas y Roadtrips en Auto desde Cancún | Guía Oficial',
      description: 'Descubre los mejores circuitos en auto saliendo de Cancún: Riviera Maya, Chichén Itzá e Isla Blanca. Tiempos de manejo, distancias y renta de autos.'
    },
    en: {
      title: 'Roadtrips & Driving Routes from Cancun | Riviera Maya',
      description: 'Explore the best scenic driving itineraries from Cancun: Riviera Maya, Chichen Itza, and Isla Blanca. Driving times, distances, and car rental tips.'
    },
    pt: {
      title: 'Roteiros e Rotas de Carro a partir de Cancún | Guia',
      description: 'Explore os melhores roteiros de carro a partir de Cancún: Riviera Maya, Chichén Itzá e Isla Blanca. Distâncias, tempos de viagem e dicas.'
    }
  },
  eventos: {
    es: {
      title: 'Eventos y Festivales en Cancún y Riviera Maya 2026',
      description: 'Calendario oficial de eventos, festivales culturales y conciertos en Cancún, Playa del Carmen y Riviera Maya: fechas, sedes y cartelera.'
    },
    en: {
      title: 'Events & Festivals in Cancun & Riviera Maya 2026',
      description: 'Complete calendar of annual events, music festivals, cultural traditions, and concerts in Cancun, Playa del Carmen and Riviera Maya.'
    },
    pt: {
      title: 'Eventos e Festivais em Cancún e Riviera Maya 2026',
      description: 'Calendário completo de eventos anuais, festivais culturais e concertos em Cancún, Playa del Carmen e Riviera Maya.'
    }
  },
  'consejos-manejo': {
    es: {
      title: 'Consejos de Manejo y Conducción Segura en Cancún',
      description: 'Consejos prácticos para manejar en Cancún: reglamento de tránsito, gasolineras, peajes, auxilio vial y calculadora de ahorro en renta de autos.'
    },
    en: {
      title: 'Driving Guide & Road Tips for Cancun | Rental Advice',
      description: 'Practical driving advice in Cancun: road rules, gas stations, highway tolls, and the Car vs Taxi Savings Calculator.'
    },
    pt: {
      title: 'Guia de Direção e Dicas para Dirigir em Cancún',
      description: 'Dicas práticas para dirigir em Cancún: regras de trânsito, postos de gasolina, pedágios e Calculadora de Economia.'
    }
  },
  'consejos-viaje': {
    es: {
      title: 'Tips y Consejos de Viaje para Cancún y Riviera Maya',
      description: 'Consejos esenciales para tus vacaciones en Cancún: mejor época para viajar, cómo evitar el sargazo, dinero, equipaje y turismo responsable.'
    },
    en: {
      title: 'Cancun Travel Tips & Essential Advice | Travel Guide',
      description: 'Essential tips for your Cancun vacation: best seasons, weather, sargassum updates, currency, packing checklist, and responsible tourism.'
    },
    pt: {
      title: 'Dicas Essenciais de Viagem para Cancún | Guia Prático',
      description: 'Dicas essenciais para suas férias em Cancún: melhor época, clima, sargaço, dinheiro, o que levar na mala e turismo responsável.'
    }
  }
};

const ROUTE_SLUGS: Record<Language, Record<string, string>> = {
  es: {
    home: '',
    '': '',
    rutas: 'rutas',
    routes: 'rutas',
    roteiros: 'rutas',
    eventos: 'eventos',
    events: 'eventos',
    'consejos-manejo': 'consejos-manejo',
    'driving-guide': 'consejos-manejo',
    'guia-direcao': 'consejos-manejo',
    'consejos-viaje': 'consejos-viaje',
    'travel-tips': 'consejos-viaje',
    'dicas-viagem': 'consejos-viaje',
  },
  en: {
    home: '',
    '': '',
    rutas: 'routes',
    routes: 'routes',
    roteiros: 'routes',
    eventos: 'events',
    events: 'events',
    'consejos-manejo': 'driving-guide',
    'driving-guide': 'driving-guide',
    'guia-direcao': 'driving-guide',
    'consejos-viaje': 'travel-tips',
    'travel-tips': 'travel-tips',
    'dicas-viagem': 'travel-tips',
  },
  pt: {
    home: '',
    '': '',
    rutas: 'roteiros',
    routes: 'roteiros',
    roteiros: 'roteiros',
    eventos: 'eventos',
    events: 'eventos',
    'consejos-manejo': 'guia-direcao',
    'driving-guide': 'guia-direcao',
    'guia-direcao': 'guia-direcao',
    'consejos-viaje': 'dicas-viagem',
    'travel-tips': 'dicas-viagem',
    'dicas-viagem': 'dicas-viagem',
  },
};

/**
 * Returns a fully-qualified language-prefixed localized path.
 * e.g. getLocalizedRoute('consejos-viaje', 'es') => '/es/consejos-viaje/'
 * e.g. getLocalizedRoute('consejos-manejo', 'en') => '/en/driving-guide/'
 * e.g. getLocalizedRoute('consejos-viaje', 'pt') => '/pt/dicas-viagem/'
 * e.g. getLocalizedRoute('home', 'es') => '/'
 */
export function getLocalizedRoute(route: string, lang: Language): string {
  const cleanKey = route.startsWith('/') ? route.slice(1).replace(/\/$/, '') : route.replace(/\/$/, '');
  const slug = ROUTE_SLUGS[lang]?.[cleanKey] !== undefined
    ? ROUTE_SLUGS[lang][cleanKey]
    : cleanKey;

  if (!slug) {
    return lang === 'es' ? '/' : `/${lang}/`;
  }
  return `/${lang}/${slug}/`;
}

/**
 * Translates the current URL pathname to the corresponding target language URL.
 * e.g. /es/consejos-manejo/ -> /en/driving-guide/
 * e.g. /en/travel-tips/ -> /pt/dicas-viagem/
 * e.g. /en/ -> /
 */
export function translateCurrentPath(currentPath: string, targetLang: Language): string {
  const parts = currentPath.split('?')[0].split('#')[0].split('/').filter(Boolean);
  if (parts.length === 0) {
    return targetLang === 'es' ? '/' : `/${targetLang}/`;
  }
  let slug = parts[0];
  if (['es', 'en', 'pt'].includes(slug)) {
    slug = parts[1] || '';
  }
  if (!slug) {
    return targetLang === 'es' ? '/' : `/${targetLang}/`;
  }
  return getLocalizedRoute(slug, targetLang);
}

export interface RouteMetadataResult {
  section: SectionKey;
  lang: Language;
  title: string;
  description: string;
  canonicalUrl: string;
  esUrl: string;
  enUrl: string;
  ptUrl: string;
  locale: string;
}

/**
 * Resolves full SEO metadata, canonicals and hreflangs for any route URL.
 */
export function getRouteMetadata(routeUrl: string): RouteMetadataResult {
  const clean = routeUrl.replace(/^\//, '').replace(/\/$/, '');
  const parts = clean.split('/').filter(Boolean);
  let lang: Language = 'es';
  let slug = parts[0] || '';
  if (['es', 'en', 'pt'].includes(slug)) {
    lang = slug as Language;
    slug = parts[1] || '';
  }

  // Determine section
  let section: SectionKey = 'home';
  if (['rutas', 'routes', 'roteiros'].includes(slug)) {
    section = 'rutas';
  } else if (['eventos', 'events'].includes(slug)) {
    section = 'eventos';
  } else if (['consejos-manejo', 'driving-guide', 'guia-direcao', 'consejos'].includes(slug)) {
    section = 'consejos-manejo';
  } else if (['consejos-viaje', 'travel-tips', 'dicas-viagem', 'viaje'].includes(slug)) {
    section = 'consejos-viaje';
  }

  let canonicalUrl = 'https://cancunmexico.travel/';
  let esUrl = 'https://cancunmexico.travel/';
  let enUrl = 'https://cancunmexico.travel/en/';
  let ptUrl = 'https://cancunmexico.travel/pt/';

  if (section === 'home') {
    if (lang === 'en') canonicalUrl = 'https://cancunmexico.travel/en/';
    else if (lang === 'pt') canonicalUrl = 'https://cancunmexico.travel/pt/';
    else canonicalUrl = 'https://cancunmexico.travel/';
  } else if (section === 'rutas') {
    esUrl = 'https://cancunmexico.travel/es/rutas/';
    enUrl = 'https://cancunmexico.travel/en/routes/';
    ptUrl = 'https://cancunmexico.travel/pt/roteiros/';
    canonicalUrl = lang === 'en' ? enUrl : lang === 'pt' ? ptUrl : esUrl;
  } else if (section === 'eventos') {
    esUrl = 'https://cancunmexico.travel/es/eventos/';
    enUrl = 'https://cancunmexico.travel/en/events/';
    ptUrl = 'https://cancunmexico.travel/pt/eventos/';
    canonicalUrl = lang === 'en' ? enUrl : lang === 'pt' ? ptUrl : esUrl;
  } else if (section === 'consejos-manejo') {
    esUrl = 'https://cancunmexico.travel/es/consejos-manejo/';
    enUrl = 'https://cancunmexico.travel/en/driving-guide/';
    ptUrl = 'https://cancunmexico.travel/pt/guia-direcao/';
    canonicalUrl = lang === 'en' ? enUrl : lang === 'pt' ? ptUrl : esUrl;
  } else if (section === 'consejos-viaje') {
    esUrl = 'https://cancunmexico.travel/es/consejos-viaje/';
    enUrl = 'https://cancunmexico.travel/en/travel-tips/';
    ptUrl = 'https://cancunmexico.travel/pt/dicas-viagem/';
    canonicalUrl = lang === 'en' ? enUrl : lang === 'pt' ? ptUrl : esUrl;
  }

  const seo = ROUTE_SEO[section][lang];
  const localeMap: Record<Language, string> = {
    es: 'es_MX',
    en: 'en_US',
    pt: 'pt_BR'
  };

  return {
    section,
    lang,
    title: seo.title,
    description: seo.description,
    canonicalUrl,
    esUrl,
    enUrl,
    ptUrl,
    locale: localeMap[lang] || 'es_MX'
  };
}
